import { Title } from "components";
import { Row, Col, Card, Button, Dropdown, SplitButton, Form } from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import { ZDateTimePicker } from "components/ZDateTimePicker";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState, store } from "redux/store";
import { AgGridReact } from 'ag-grid-react';
import { cleanReports, generateSalesReport, generateStockReport, getReport } from "redux/slices/Reports";
import { salesHeaders } from "TableConfigs/StoreSalesReportConfig";
import { ColDef, IServerSideDatasource } from "ag-grid-community";
import PriceCellRenderer from "components/AgGrid/CellRenderers/PriceCellRenderer";
import PercentCellRenderer from "components/AgGrid/CellRenderers/PercentCellRenderer";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import { StockReportFunction } from "models/functionModels/reportFunctions/StockReportFunction";
import { SockQtySoldTime, stockHeaders } from "TableConfigs/StoreStockReportConfig";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ZTextInput } from "components/ZTextInput";
import { getExportStockReport, getNormalSalesReportExport } from "services/reportservices";
import { TimedMessage } from "components/TimedMessage";
import DateCellRenderer from "components/AgGrid/CellRenderers/DateCellRenderer";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { ZTooltip } from "components/ZTooltip";
import { endOfDay } from "date-fns";
import {} from "helpers/dateFormater";
import IntegerCellRenderer from "components/AgGrid/CellRenderers/IntegerCellRenderer";

export function ReportViewer() {
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [maxEndDate, setMaxEndDate] = useState<Date | null>(null);
    const [endDateError, setEndDateError] = useState<string | null>(null);
    const [startDateError, setStartDateError] = useState<string | null>();
    const [keyword, setKeyword] = useState<string>("");
    const stores = useAppSelector((state: RootState) => state.stores);
    const dispatch = useAppDispatch();
    const salesReportGridRef = useRef<AgGridReact>(null);
    const [selectedStore, setSelectedStore] = useState<number | null>(null);
    const [reportType, setReportType] = useState<string | null>(null);
    const {reportId} =  useParams();
    const [reportColumns, setReportColumns] = useState<ColDef<any, any>[]>([]);
    const [reportQuery, setReportQuery] = useState<any>();
    const [reportDescription, setReportDescription] = useState<string>("");
    const [reportName, setReportName] = useState<string>("");
    const [dateRangeDisabled, setDateRangeDisabled] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const urlStartDate = searchParams.get("startDate") ? new Date(searchParams.get("startDate")!) : null;
    const urlEndDate = searchParams.get("endDate") ? new Date(searchParams.get("endDate")!) : null;
    const urlStoreId = searchParams.get("storeId") ? parseInt(searchParams.get("storeId")!) : null;
    const generate = searchParams.get("generate") ? searchParams.get("generate") === "true" : false;
    const [selectedSearch, setSelectedSearch] = useState<{columnName: string|undefined, field:string|undefined} | undefined>();
    const [avalableSearchFields, setAvalableSearchFields] = useState<{columnName: string|undefined, field:string|undefined}[] | undefined>();
    const [searchField, setSearchField] = useState<string>("");
    const [reportGenerated, setReportGenerated] = useState<boolean>(false);
 
	useEffect(() => {
		dispatch(cleanReports());
        setReportGenerated(false);
		dispatch(getReport({ ids: [parseInt(reportId || '')] })).then((res) => LoadColumns(res));
    }, []);

    useEffect(() => {
        if (salesReportGridRef.current && salesReportGridRef.current.api) {
            salesReportGridRef.current.api.showNoRowsOverlay();
        }
    }, [salesReportGridRef.current?.api]);

    useEffect(() => {
        if (reportQuery && generate) {
            generateReport();
        }
    }, [reportQuery]);

    useEffect(() => {
        if (urlStartDate) {
            setStartDate(urlStartDate);
        }
        if (urlEndDate) {
            setEndDate(urlEndDate);
        }
        if (urlStoreId) {
            setSelectedStore(urlStoreId);
        }
    }, [searchParams]);

    useEffect(()=>{
        if((reportType === "Stock" || reportType === "StockExperimental") && startDate && reportQuery.designReportColumns.filter((x: any) => x.type === "STOCK_QTY_SOLD_TIME").length > 0){
            let maxDate = new Date(startDate.toDateString());
            maxDate.setMonth(maxDate.getMonth() + 13);
            setMaxEndDate(maxDate);
        }
    }, [startDate])

    
    function applyGridConfigeration(configString: string, columns: ColDef<any,any>[]){
        if(configString == undefined || configString == "")
            return;
        const config = JSON.parse(configString);
        if(config.PrimaryGroupBy != undefined){
            let primaryColumnToGroupBy = columns.filter((col: ColDef<any,any> )=> col.field == config.PrimaryGroupBy)[0];
            primaryColumnToGroupBy.rowGroup = true;
            primaryColumnToGroupBy.hide = true;
        }
        if(config.SecondaryGroupBy != undefined){
            let secondaryGroupBy = columns.filter((col: ColDef<any,any> )=> col.field == config.SecondaryGroupBy)[0];
            secondaryGroupBy.rowGroup = true;
            secondaryGroupBy.hide = true;
        }
        if(config.SortBYColumn != undefined){
            let sortBy = columns.filter((col: ColDef<any,any> )=> col.field == config.SortBYColumn)[0];
            let sortByDesc = config.SortByDesc == undefined? false:  config.SortByDesc == true;
            sortBy.sort = sortByDesc? "desc": "asc";
        }       
        if(config.DefaultSearchColumn != undefined){
            let searchBy = columns.filter((col: ColDef<any,any> )=> col.field == config.DefaultSearchColumn)[0];
            setSelectedSearch({columnName: searchBy.headerName, field: searchBy.field});            
        }
        if(config.SearchableColumnList != undefined){
            let availableSearchColumns: {columnName: string|undefined, field:string|undefined}[] = [];
            config.SearchableColumnList.forEach((colField: string) => {             
                let searchColumn = columns.filter((col: ColDef<any,any> )=> col.field == colField)[0];
                availableSearchColumns.push({columnName: searchColumn.headerName, field: searchColumn.field});
            });
            setAvalableSearchFields(availableSearchColumns);
        }
    }

    /// filters the stock column list based on what the filter includes from the api
    function filterStockColumns(filter: any): ColDef<any, any>[] {
        let columns: ColDef<any, any>[] = [];
        let numberOfCustomStockColumns = 0;
        for(let i = 0; filter.designReportColumns.length > i; i++){
            if(filter.designReportColumns[i].type === "STOCK_CUSTOM"){                
                const header: ColDef<any, any> = {
                    headerName : filter.designReportColumns[i].name,
                    field: numberOfCustomStockColumns == 0? "STOCK_CUSTOM":filter.designReportColumns[i].type + "_" + numberOfCustomStockColumns,
                    type: ['rightAligned'],
                    minWidth: 195,                    
                    cellRendererParams: { align: "right" }, 
                    cellRendererSelector: filter.designReportColumns[i].name.includes("%")? (_) => { return { component: "percentCellRenderer" }} : (_) => { return { component: "priceCellRenderer" }}
                };
                columns.push(header);
                numberOfCustomStockColumns ++;
            }
            else if (filter.designReportColumns[i].type === "STOCK_QTY_SOLD_TIME"){
                SockQtySoldTime.forEach(x => columns.push(x));
            }
            else{
                columns.push(stockHeaders.find(x => x.field === filter.designReportColumns[i].type)!);
            }
        }
        //console.log("Loading columns",columns);
        return columns;
    }

    /// filters the sales column list based on what the filter includes from the api
    function filterSalesColumns(filter: any): ColDef<any, any>[] {
        let columns: ColDef<any, any>[] = [];
        let numberOfCustomSalesColumns = 0;
            for (let i = 0; filter.designReportColumns.length > i; i++) {
                if (filter.designReportColumns[i].type === "SALES_CUSTOM") {
                    numberOfCustomSalesColumns ++;
                    const header: ColDef<any, any> = {
                        headerName : filter.designReportColumns[i].name,
                        field: filter.designReportColumns[i].type + "_" + numberOfCustomSalesColumns,                     
                        cellRendererParams: { align: "right" }, 
                        cellRendererSelector: filter.designReportColumns[i].name.includes("%")? (_) => { return { component: "percentCellRenderer" }} : (_) => { return { component: "priceCellRenderer" }}
                    };
                    columns.push(header);
                } else {
                    columns.push(salesHeaders.find(x => x.field === filter.designReportColumns[i].type)!);
                }
            }

        return columns;
    }

    /// sets up columns based of ID from the query param in the url
    function LoadColumns(res: any) {
        const filter = JSON.parse(res.filter);
        let columns: ColDef<any, any>[] = [];
        if (res.reportType.toUpperCase() === "Stock".toUpperCase()){
            columns = filterStockColumns(filter);
        }
        else
        {            
            columns = filterSalesColumns(filter);
        }
        if(filter.startDateTime !== null && searchParams.get("startDate") == null) {
            // set start date to start of this month, ignore filter start date time
            setStartDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
        }
        if(filter.endDateTime !== null && searchParams.get("endDate") == null) {
            // set end date to end of this month, ignore filter end date time
            setEndDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
        }
        if(res.gridConfiguration != undefined){
            applyGridConfigeration(res.gridConfiguration, columns);
        }

        setReportName(res.name);
        setReportDescription(res.description);
        setReportColumns(columns);
        setReportType(res.reportType);
        setReportQuery(filter);
	}

    /// Does the api call for report data. also handles grouping requests
    const getServerSideDatasource: () => IServerSideDatasource = () => {
        return {
            getRows: async (params) => {
                params.api.showLoadingOverlay();
                if(reportColumns.length == 0){
                    params.api.showNoRowsOverlay();
                    params.success({
                        rowData: [],
                        rowCount: 0,
                    })
                    return;
                }
                let groupByColumns: any[] = params.request.rowGroupCols;                

                let requestParamas: any = getQueryWithSearchParams(params);
                if (params.request.sortModel != null && params.request.sortModel.length > 0 && params.request.sortModel[0].colId != "ag-Grid-AutoColumn") {
                    requestParamas.sortByColumn = params.request.sortModel[0].colId.replace("ag-Grid-AutoColumn-","");
                    requestParamas.sortByDesc = params.request.sortModel[0].sort === "desc";
                    requestParamas.OrderByColumn = params.request.sortModel[0].colId.replace("ag-Grid-AutoColumn-","");
                    requestParamas.OrderByDesc = params.request.sortModel[0].sort === "desc";                    
                }
                if (groupByColumns != undefined && groupByColumns?.length > 0) {
                    requestParamas.groupByColumn = groupByColumns[0].field;
                    requestParamas.groupByColumnDesc = groupByColumns[0].sort === "desc";
                    requestParamas.secondGroupByColumn = groupByColumns.length > 1? groupByColumns[1].field: null;
                }                
                if (groupByColumns != undefined && params.request.groupKeys != undefined && params.request.groupKeys?.length == 1) {
                    requestParamas.groupingDataMatch = [{ reportColumnName: groupByColumns[0].field, fieldValue: params.request.groupKeys[0]}];
                } 
                else if (groupByColumns != undefined && params.request.groupKeys != undefined && params.request.groupKeys?.length == groupByColumns.length) {
                    requestParamas.groupingDataMatch = [];
                    requestParamas.groupByColumn = null;

                    for (let i = 0;  params.request.groupKeys.length > i; i++) {
                        requestParamas.groupingDataMatch?.push({ reportColumnName: groupByColumns[i].field, fieldValue: params.request.groupKeys[i]})
                    }
                }

                let response: any = [];

                if ((reportType === "Sales" || reportType === "SalesExperimental")) {
                    response = await dispatch(generateSalesReport(requestParamas));
                }
                else {
                    params.api.hideOverlay();
                    response = await dispatch(generateStockReport(requestParamas as StockReportFunction))
                        .catch(error =>{
                            console.log("Error loading report data", error);
                            params.fail();
                            return;
                        });
                }

                if (response?.response?.data?.rows == null) {
                    params.api.showNoRowsOverlay();
                    params.success({
                        rowData: [],
                        rowCount: 0
                    });
                    return;
                }
             
                if(response.response.data.displayColumns != null && reportQuery.designReportColumns.filter((x: any) => x.type == "STOCK_QTY_SOLD_TIME").length > 0){
                    const upperDisplayColumns = Object.fromEntries(Object.entries(response.response.data.displayColumns).map(([key, value]) => [key.toUpperCase(), value as string]));
                    let allColumns = salesReportGridRef.current?.api.getColumnDefs()?.filter(x => true);
                    let columnsNeedingName = allColumns?.filter((ro: ColDef) => ro.field!.includes("STOCK_QTY_SOLD_TIME"));
                    columnsNeedingName?.forEach((col: ColDef) => {
                        if(upperDisplayColumns[col.field!.toUpperCase()] === undefined){
                            col.hide = true;
                        }
                        else{
                            col.hide = false;
                            col.headerName = upperDisplayColumns[col.field!.toUpperCase()];
                        }

                    });                    
                    salesReportGridRef.current?.api.setGridOption("columnDefs", allColumns);
                }

                params.api.hideOverlay();

                const normalisedRows = response?.response?.data?.rows.map((row: any) => {
                    const normalised: any = {};
                    Object.keys(row).forEach(key => {
                        //normalised[key] = row[key];
                        normalised[key.toUpperCase()] = row[key];
                    });
                    //console.log("Normalised row", normalised);
                    return normalised;
                })

                params.success({
                    rowData: normalisedRows,
                    rowCount: response?.response?.totalRows,
                })
            },
        };
    };

    function getQueryWithSearchParams(params: any|null): any{
        let requestParamas: any = {
            filterCriteria: {...reportQuery.filterCriteria, searchString: searchField, containsSearch: true, searchName: true, searchPDE: true, searchBarcode: true  },
            overrideFilterDates: reportQuery.overrideFilterDates,
            designReportColumns: reportQuery.designReportColumns,
            topN: reportQuery.TopN,
            groupByColumn1: reportQuery.groupByColumn1,
            groupByColumn2: reportQuery.groupByColumn2,
            groupByColumn: null,              
            secondGroupByColumn: null,
            startDateTime: startDate? startDate.GetLocalDateNoTimezone(): null, 
            endDateTime: endDate? endOfDay(endDate).GetLocalDateNoTimezone(): null,
            timePeriodType: reportQuery.timePeriodType,
            storeIds: selectedStore == null? null: [selectedStore],
            offset: params === null? null: params.request.startRow!,
            limit: params === null? null: (params.request.endRow! - params.request.startRow!),
            timeRangeType: reportQuery.timeRangeType,
            orderByColumn: reportQuery.orderByColumn,
            orderByDesc: reportQuery.orderByDesc,          
            startTime: reportQuery.startTime,                   
            defaultStartDate: reportQuery.defaultStartDate,
            defaultStartDateOffset: reportQuery.defaultStartDateOffset,
            defaultStartDatePeriod: reportQuery.defaultStartDatePeriod,
            defaultEndDate: reportQuery.defaultEndDate,
            defaultEndDateOffset: reportQuery.defaultEndDateOffset,
            defaultEndDatePeriod: reportQuery.defaultEndDatePeriod,
            groupByDesc1: reportQuery.groupByDesc1,
            groupByDesc2: reportQuery.groupByDesc2,
            showTotalsOnly: reportQuery.showTotalsOnly,
            showMostFrequentInGroupTotals: reportQuery.showMostFrequentInGroupTotals,
            averageTimeTotals: reportQuery.averageTimeTotals,
            filterFields: selectedSearch == undefined || searchField == ""? null :[ { 'filterColumnName': selectedSearch.field, 'fieldValue': searchField}]
        };

        return requestParamas;
    }

    ///used for the generate report button
    function generateReport() {
        setReportGenerated(true);
        var datasource = getServerSideDatasource();
        salesReportGridRef.current?.api.setGridOption("serverSideDatasource", datasource);
    }

    ///used to stop more than 2 columns from grouping
    function agGridGroupingRestricter(){       
        const groupedRows = salesReportGridRef.current?.api.getColumnDefs()?.filter((x: ColDef )=> x.rowGroup === true);
        if(groupedRows == null || groupedRows.length == 0 || groupedRows.length == 1 && salesReportGridRef.current?.api.getColumnDefs()?.filter((x: ColDef )=> x.enableRowGroup === false).length == 0){
            return;
        }
        let allColumns = salesReportGridRef.current?.api.getColumnDefs()?.filter(tz => true);
        if(groupedRows != null && groupedRows.length == 2){            
            var lockColumns = allColumns?.filter((x: ColDef )=> x.rowGroup === false && x.enableRowGroup === true);
            lockColumns?.forEach((lck: ColDef)=> {
                lck.enableRowGroup = false;
            });
        }
        if(groupedRows != null && groupedRows.length == 1){
            var unLockColumns = allColumns?.filter((x: ColDef )=> x.rowGroup === false && x.enableRowGroup === false);
            unLockColumns?.forEach((lck: ColDef)=> {
                lck.enableRowGroup = true;
            }); 
        }
        salesReportGridRef.current?.api.setGridOption("columnDefs", allColumns);
    }

    async function ExportReport() {		
		setExporting(true);
	
		if (reportType === "Stock" || reportType === "StockExperimental") {
			getExportStockReport(getQueryWithSearchParams(null), reportName, reportDescription)
                .then(()=>{setExporting(false)});
				
		} else if (reportType == 'Sales' || reportType === "SalesExperimental") {
			getNormalSalesReportExport(getQueryWithSearchParams(null), reportName, reportDescription)
                .then(()=>{setExporting(false)});
		
		}
	}

    const showTable = useMemo(() => {        
        return <>
            <div style={{height: 500}}>
                <AgGridReact
                    ref={salesReportGridRef}
                    className={`ag-theme-quartz ag-theme-custom w-100`}
                    rowModelType="serverSide"
                    rowGroupPanelShow= 'always'
                    pagination={true}
                    suppressAggFuncInHeader={true}
                    paginationPageSize={20}
                    cacheBlockSize={100}
                    groupDisplayType='multipleColumns'                                    
                    columnDefs={reportColumns}
                    onColumnRowGroupChanged={()=>agGridGroupingRestricter()}
                    getChildCount={ (data: any) => {
                        return data.ROW_COUNT;
                    }}                       
                    defaultColDef= {{
                        flex: 1,
                        minWidth: 150
                    }}
                    components={{
                        baseCellRenderer: BaseCellRenderer,
                        priceCellRenderer: PriceCellRenderer, 
                        percentCellRenderer: PercentCellRenderer,
                        dateCellRenderer: DateCellRenderer,
                        integerCellRenderer: IntegerCellRenderer
                    }}
                    localeText = {{"rowGroupColumnsEmptyMessage": "Drag up to 2 columns here to set row groups"}}
                />
            </div>
        </>
    },[reportColumns]);

    const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);

    return (
		<>
        <Title pageTitle={reportName} crumbs={[{ name: "Reports", path: "/app/reports" }]} />
        <Row>           
            <Col xs={12}>
                <Card>
                    <Card.Body>
                        <Row className="d-flex align-items-end mb-1 gap-y-1">
                            <Col xs={12} sm={4}>
                                <ZSingleSelectDropdown 
                                    options={stores.table}
                                    optionKey={s => s.id}
                                    value={stores.table.find(s => s.id === selectedStore)}
                                    display={s => s.name}
                                    onSelectionChange={s => s && setSelectedStore(s.id)}
                                    disabled={s => !s?.connected}
                                    searchPlaceholder="Search stores"
                                    placeholder="Select store"
                                    label="Store:"
                                    hasSearch
                                />
                            </Col>
                            <Col xs={0} sm={2}>
                            </Col>
                            <Col xs={12} sm={3}>
                                <ZDateTimePicker
                                    canClear={true}
                                    value={startDate}
                                    label="Start Date"
                                    error={startDateError}
                                    onError={(e) => setStartDateError(e)}
                                    onChanged={(d) => setStartDate(d)}
                                    hasTime={false}
                                    disabled={dateRangeDisabled}
                                    maxDate={endDate == null? undefined: endDate}
                                    nullable={(reportType === "Stock" || reportType === "StockExperimental")}
                                />
                            </Col>
                            <Col xs={12} sm={3}>
                                <ZDateTimePicker
                                    canClear={true}
                                    value={endDate}
                                    label="End Date"
                                    error={endDateError}
                                    onError={(e) => setEndDateError(e)}
                                    onChanged={(d) => setEndDate(d)}
                                    hasTime={false}
                                    disabled={dateRangeDisabled}
                                    minDate={startDate == null? undefined: startDate}
                                    maxDate={maxEndDate == null? undefined: maxEndDate}
                                    nullable={(reportType === "Stock" || reportType === "StockExperimental")}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} sm={12}>
                                    <div className="input-group mb-1">
                                        <input disabled={selectedStore == null} type="text" className="form-control" placeholder={selectedSearch ? `Search by ${selectedSearch?.columnName}` : 'Search'} aria-describedby="button-addon2" onChange={(e)=>setSearchField(e.currentTarget.value)}
                                            onKeyDown={(e)=>{
                                                if(e.key == 'Enter')
                                                    generateReport();
                                            }}
                                        >
                                            
                                        </input>
                                                <ZTooltip enabled={selectedStore == null} text="Please select a store to search">
                                        <SplitButton
                                            variant="primary"
                                            title={`Search`}
                                            onClick={generateReport}
                                            className="custom-search-button"
                                            show={searchDropdownOpen}
                                            disabled={selectedStore == null}
                                            autoClose="outside"
                                            onToggle={() => setSearchDropdownOpen(!searchDropdownOpen)}
                                        > 
                                            {
                                                avalableSearchFields?.map(i => (
                                                    <Dropdown.Item eventKey={i.field} onClick={()=>setSelectedSearch({columnName: i.columnName, field: i.field})}>
                                                    <Form.Check
                                                        type="radio"
                                                        label={i.columnName}
                                                        checked={selectedSearch?.field === i.field}
                                                        onClick={() => setSelectedSearch({columnName: i.columnName, field: i.field})}
                                                    /></Dropdown.Item>
                                                ))
                                            }
                                        </SplitButton>
                                </ZTooltip>
                                    </div>
                            </Col>
                        </Row>
                        <Row className="d-flex align-items-end mb-1">                                   
                            <Col xs={12}>
                                <ZTextInput												
                                    label="Description:"
                                    type="text"
                                    placeholder="Enter report description"
                                    disabled = {true}
                                    value={reportDescription}
                                    key="text"										
                                />
                            </Col>
                        </Row>
                        <Row>
                        <Col xs={12}>
                           {showTable}
                        </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col sm={8}>
                                <Link to="/app/reports" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
                                    <i className="mdi mdi-arrow-left"></i> Back to Reports {' '}
                                </Link>
                            </Col>
                            <Col xs={4} className="d-flex flex-reverse-row justify-content-end">
                                <TimedMessage message="Please wait while we export your report..." delay={5} enabled={exporting} className="mb-0 py-1 px-2 ms-auto" />
                                <ZTooltip enabled={!reportGenerated} text={"Please generate a report to export"}>
                                    <Button className="ms-1 cta-button" variant="light" disabled={exporting || !reportGenerated} onClick={ExportReport}>
                                        Export
                                    </Button>
                                </ZTooltip>
                                <ZTooltip enabled={selectedStore == null || startDateError != null || endDateError != null} text={startDateError != null || endDateError != null ? `Invalid dates` : "Please select a store to generate report for"}>
                                    <Button className="ms-1 cta-button" disabled={selectedStore == null || startDateError != null || endDateError != null} onClick={()=>generateReport()}>Generate</Button>
                                </ZTooltip>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </>
    );
}